import type { LayeredFormat } from "src/pages/ad-studio/psd-export/build-email-layered";
import {
  type EmailExportFormat,
  formatList,
} from "src/pages/lifecycle-studio/emails/png-export/export-email";

export type FormatOption<T extends string> = {
  value: T;
  label: string;
  description: string;
  isDisabled?: boolean;
};

export type EmailFormat = "html" | "pdf" | EmailExportFormat;

export const EMAIL_FORMAT_OPTIONS: FormatOption<EmailFormat>[] = [
  {
    value: "html",
    label: "HTML",
    description:
      "Email markup, with dynamic content compiled for your email provider",
  },
  {
    value: "pdf",
    label: "PDF",
    description: "A proof for review, with content blocks expanded",
  },
  {
    value: "png",
    label: "PNG",
    description: "Images at web and mobile widths, in light and dark mode",
  },
  {
    value: "psd",
    label: "PSD",
    description: "Layered Photoshop files at web and mobile widths",
  },
  {
    value: "idml",
    label: "IDML",
    description: "Layered InDesign files at web and mobile widths",
  },
];

export function isEmailExportFormat(
  format: EmailFormat,
): format is EmailExportFormat {
  return format !== "html" && format !== "pdf";
}

export type WebPageFormat = "html" | LayeredFormat;

export const WEB_PAGE_FORMAT_OPTIONS: FormatOption<WebPageFormat>[] = [
  {
    value: "html",
    label: "HTML",
    description: "A ZIP of the page markup and its images",
  },
  {
    value: "psd",
    label: "PSD",
    description: "Layered Photoshop files at desktop and mobile widths",
  },
  {
    value: "idml",
    label: "IDML",
    description: "Layered InDesign files at desktop and mobile widths",
  },
];

export type DocFormat = "md" | "pdf";

export const DOC_FORMAT_OPTIONS: FormatOption<DocFormat>[] = [
  {
    value: "md",
    label: "Markdown",
    description: "The text with its formatting, for editors and other tools",
  },
  {
    value: "pdf",
    label: "PDF",
    description: "A formatted copy to share or print",
  },
];

/** A bulk download warning that names the file and the formats it couldn't export. */
export function exportFailureWarning(
  name: string,
  formats: readonly string[],
  error: unknown,
) {
  return `${name} couldn't be exported as ${formatList(formats)}${error instanceof Error ? `: ${error.message}` : "."}`;
}
